import { useOrg } from '../context/OrgContext';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SearchableSelect from '../components/SearchableSelect';
import { PDMeasurementPopup } from '../components/PDMeasurementPopup';
import { Camera } from 'lucide-react';
import { storePrescriptionFile } from '../utils/prescriptionStore';

type LensType = 'normal' | 'single_vision' | 'progressive' | 'bifocal' | '';
type CoatingType = 'normal' | 'anti_glare' | 'blue_light_filter' | 'uv_protection' | '';

interface EyePowerValue { sphere: string; cylinder: string; axis: string; }
interface EyePower { leftEye: EyePowerValue; rightEye: EyePowerValue; }

const LENS_TYPES: LensType[] = ['normal', 'single_vision', 'progressive', 'bifocal'];
const COATING_TYPES: CoatingType[] = ['normal', 'anti_glare', 'blue_light_filter', 'uv_protection'];
const STEPS = ['Lens Type', 'Eye Power', 'Coating'];

const SPHERE_OPTIONS = Array.from({ length: 161 }, (_, i) => {
  const v = (i - 80) / 4;
  return v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2);
});
const CYLINDER_OPTIONS = Array.from({ length: 49 }, (_, i) => {
  const v = (i - 24) / 4;
  return v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2);
});
const AXIS_OPTIONS = Array.from({ length: 180 }, (_, i) => String(i + 1));

const lensLabels: Record<string, string> = {
  normal: 'Normal (No Power)',
  single_vision: 'Single Vision',
  progressive: 'Progressive',
  bifocal: 'Bifocal',
};

const coatingLabels: Record<string, string> = {
  normal: 'No Coating',
  anti_glare: 'Anti Glare',
  blue_light_filter: 'Blue Light Filter',
  uv_protection: 'UV Protection',
};

const COATING_PRICES: Record<string, number> = {
  normal: 999,
  anti_glare: 899,
  blue_light_filter: 1090,
  uv_protection: 1500,
};

const LensSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, openLogin } = useAuth();

  const [step, setStep] = useState(1);
  const [lensType, setLensType] = useState<LensType>('');
  const [coating, setCoating] = useState<CoatingType>('');
  const [eyePower, setEyePower] = useState<EyePower>({
    leftEye: { sphere: '', cylinder: '', axis: '' },
    rightEye: { sphere: '', cylinder: '', axis: '' },
  });
  const [pdMeasurement, setPdMeasurement] = useState<string>(localStorage.getItem('measuredPD') || '');
  const [leftPdMeasurement, setLeftPdMeasurement] = useState<string>(localStorage.getItem('measuredLeftPD') || '');
  const [rightPdMeasurement, setRightPdMeasurement] = useState<string>(localStorage.getItem('measuredRightPD') || '');
  const [prescriptionFile, setPrescriptionFile] = useState<File | null>(null);
  const [isPdPopupOpen, setIsPdPopupOpen] = useState<boolean>(false);
  const org = useOrg();
  const showPdMeasurement = org?.extra_fields?.pd_measurement === true;

  useEffect(() => {
    if (!user) { openLogin(); }
    const saved = localStorage.getItem('lensSelectionData');
    if (saved) {
      try {
        const d = JSON.parse(saved);
        if (d.lensType) setLensType(d.lensType);
        if (d.coating) setCoating(d.coating);
        if (d.eyePower) setEyePower(d.eyePower);

        if (d.pdMeasurement) setPdMeasurement(d.pdMeasurement);
        if (d.leftPdMeasurement) setLeftPdMeasurement(d.leftPdMeasurement);
        if (d.rightPdMeasurement) setRightPdMeasurement(d.rightPdMeasurement);
      } catch { }
    }
    const savedPd = localStorage.getItem('userPDValue');
    if (savedPd && !pdMeasurement) {
      setPdMeasurement(savedPd);
    }

  }, [openLogin, user]);

  const handleLensTypeSelect = (type: LensType) => {
    setLensType(type);
    if (type === 'normal') {
      setStep(3);
    } else {
      setStep(2);
    }
  };

  const handleEyePowerChange = (
    eye: keyof EyePower,
    measure: keyof EyePowerValue,
    value: string
  ) => {
    setEyePower((prev) => ({ ...prev, [eye]: { ...prev[eye], [measure]: value } }));
  };

  const handleContinue = () => {
    // Store the File object in memory so AddressPage can upload it at order time
    storePrescriptionFile('legacy', prescriptionFile);

    localStorage.setItem('lensSelectionData', JSON.stringify({
      lensType,
      coating,
      eyePower,
      hasPrescription: !!prescriptionFile,
      coatingPrice: coating ? COATING_PRICES[coating] ?? 0 : 0,
      pdMeasurement,
      leftPdMeasurement,
      rightPdMeasurement,
    }));
    navigate('/address');
  };

  const prevStep = () => {
    if (step === 3 && lensType === 'normal') { setStep(1); setLensType(''); }
    else { setStep((p) => Math.max(p - 1, 1)); }
  };

  return (
    <div className="lens-page">
      <div className="lens-page__inner">
        <h2 className="lens-page__title">Customize Your Lenses</h2>

        {/* Stepper */}
        <div className="lens-stepper">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className={`lens-step ${step > i ? 'lens-step--done' : ''} ${step === i + 1 ? 'lens-step--active' : ''}`}>
                <div className="lens-step__circle">{step > i + 1 ? '✓' : i + 1}</div>
                <span className="lens-step__label">{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`lens-step__line ${step > i + 1 ? 'lens-step__line--done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="lens-content">
          {/* Step 1: Lens Type */}
          {step === 1 && (
            <div className="lens-grid">
              {LENS_TYPES.map((type) => (
                <button
                  key={type}
                  className={`lens-card ${lensType === type ? 'lens-card--selected' : ''}`}
                  onClick={() => handleLensTypeSelect(type)}
                >
                  <div className="lens-card__img-wrap">
                    <img src={`/${type}.png`} alt={type} className="lens-card__img" />
                  </div>
                  <span className="lens-card__label">{lensLabels[type]}</span>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Eye Power */}
          {step === 2 && (
            <div className="lens-power">
              <div className="lens-power__eyes">
                {(['leftEye', 'rightEye'] as const).map((eye) => (
                  <div key={eye} className="lens-power__eye">
                    <h4 className="lens-power__eye-title">{eye === 'leftEye' ? 'Left Eye' : 'Right Eye'}</h4>
                    {(['sphere', 'cylinder', 'axis'] as const).map((measure) => (
                      <div key={measure} className="lens-power__row">
                        <label className="lens-power__label">{measure.charAt(0).toUpperCase() + measure.slice(1)}</label>
                        <SearchableSelect
                          options={measure === 'sphere' ? SPHERE_OPTIONS : measure === 'cylinder' ? CYLINDER_OPTIONS : AXIS_OPTIONS}
                          value={eyePower[eye][measure]}
                          placeholder="Select"
                          onChange={(val) => handleEyePowerChange(eye, measure, val)}
                          className="lens-power__select"
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>


              {/* PD Measurement */}
              <div className="lens-power__pd" style={{ marginTop: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 className="lens-power__eye-title" style={{ margin: 0 }}>PD Measurement (Optional)</h4>
                  {showPdMeasurement && (
                    <button
                      type="button"
                      onClick={() => setIsPdPopupOpen(true)}
                      style={{ padding: '6px 12px', background: 'var(--color-primary, #0d0d0d)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="2" y1="12" x2="22" y2="12"></line><line x1="6" y1="8" x2="6" y2="16"></line><line x1="18" y1="8" x2="18" y2="16"></line></svg>
                      Measure PD
                    </button>
                  )}
                </div>

                <div className="lens-power__row" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <label className="lens-power__label" style={{ minWidth: '120px', flexShrink: 0 }}>I PD mm</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="e.g. 63.00"
                    value={pdMeasurement}
                    onChange={(e) => setPdMeasurement(e.target.value)}
                    style={{ flex: '1 1 100px', minWidth: '80px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>

                <div className="lens-power__row" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 150px' }}>
                    <label className="lens-power__label" style={{ minWidth: '60px' }}>L PD mm</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="31.50"
                      value={leftPdMeasurement}
                      onChange={(e) => setLeftPdMeasurement(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 150px' }}>
                    <label className="lens-power__label" style={{ minWidth: '60px' }}>R PD mm</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="31.50"
                      value={rightPdMeasurement}
                      onChange={(e) => setRightPdMeasurement(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    />
                  </div>
                </div>
              </div>

              {/* Prescription Upload */}

              <div className="lens-power__upload">
                <div className={`lens-upload-box ${prescriptionFile ? 'lens-upload-box--filled' : ''}`}>
                  {prescriptionFile ? (
                    <>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.5">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                      <p style={{ color: '#16a34a', fontWeight: 600 }}>File Selected</p>
                      <span className="lens-upload-box__name">{prescriptionFile.name}</span>
                      <button
                        className="lens-upload-box__remove"
                        onClick={() => setPrescriptionFile(null)}
                        type="button"
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <p>Upload Prescription</p>
                      {/* <p className="lens-upload-box__hint">JPG, PNG or PDF</p> */}
                      <label className="lens-upload-box__label">
                        Choose File
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => setPrescriptionFile(e.target.files?.[0] || null)}
                        />
                      </label>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Coating */}
          {step === 3 && (
            <div className="lens-grid">
              {COATING_TYPES.map((coat) => (
                <button
                  key={coat}
                  className={`lens-card ${coating === coat ? 'lens-card--selected' : ''}`}
                  onClick={() => setCoating(coat)}
                >
                  <div className="lens-card__img-wrap">
                    <img src={`/${coat}.png`} alt={coat} className="lens-card__img" />
                  </div>
                  <span className="lens-card__label">{coatingLabels[coat]}</span>
                  <span className="lens-card__price">₹{COATING_PRICES[coat].toLocaleString()}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="lens-page__footer">
          {step > 1 && (
            <button className="btn btn--outline" onClick={prevStep}>Back</button>
          )}
          {step < STEPS.length ? (
            <button
              className="btn btn--primary"
              style={{ marginLeft: 'auto' }}
              onClick={() => setStep((p) => Math.min(p + 1, STEPS.length))}
              disabled={step === 1 && !lensType}
            >
              Next
            </button>
          ) : (
            <button
              className="btn btn--primary"
              style={{ marginLeft: 'auto' }}
              onClick={handleContinue}
              disabled={!coating}
            >
              Continue to Checkout
            </button>
          )}
        </div>
      </div>
      <PDMeasurementPopup
        isOpen={isPdPopupOpen}
        onClose={() => setIsPdPopupOpen(false)}
        onComplete={(result) => {
          setPdMeasurement(result.pd.toString());
          localStorage.setItem('measuredPD', result.pd.toString());
          if (result.leftPd) {
            setLeftPdMeasurement(result.leftPd.toString());
            localStorage.setItem('measuredLeftPD', result.leftPd.toString());
          }
          if (result.rightPd) {
            setRightPdMeasurement(result.rightPd.toString());
            localStorage.setItem('measuredRightPD', result.rightPd.toString());
          }
          setIsPdPopupOpen(false);
        }}
      />
    </div>
  );
};

export default LensSelectionPage;
